import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Trash2, Search, ShieldCheck } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const ConfigurationExoneration = () => {
  const { filialeId, isConsolidated, currentFiliale } = useFiliale();
  const [filiales, setFiliales] = useState([]);
  const [allTitres, setAllTitres] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExclusion, setSelectedExclusion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilialeFilter, setSelectedFilialeFilter] = useState('');
  const [formData, setFormData] = useState({
    filiale_id: '',
    titre_id: '',
    type_exclusion: 'ALL'
  });

  const typeExclusionOptions = [
    { value: 'ALL', label: 'Toutes les charges' },
    { value: 'IPTS', label: 'IPTS (IRPP)' },
    { value: 'TAXE_COMMUNALE', label: 'Taxe communale' },
    { value: 'REDEVANCE_AV', label: 'Redevance AV' },
    { value: 'CREDIT_FONCIER', label: 'Crédit foncier' },
  ];

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les filiales
      const filialesRes = await http.get('/api/filiales');
      setFiliales(filialesRes.data || []);

      // Récupérer les titres
      const titresRes = await http.get('/api/titres');
      setAllTitres(Array.isArray(titresRes.data) ? titresRes.data : titresRes.data || []);

      // Récupérer les exonérations
      let endpoint = '/api/exclusion-filiale';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `?filiale_id=${filialeId}`;
        setSelectedFilialeFilter(filialeId);
      }
      const exclusionsRes = await http.get(endpoint);
      setExclusions(exclusionsRes.data || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filialeId, isConsolidated]);

  const handleCreate = () => {
    setFormData({
      filiale_id: isConsolidated || filialeId === 'all' ? '' : filialeId,
      titre_id: '',
      type_exclusion: 'ALL'
    });
    setShowModal(true);
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      filiale_id: isConsolidated || filialeId === 'all' ? '' : filialeId
    }));
  }, [filialeId, isConsolidated]);

  const handleDelete = (exclusion) => {
    setSelectedExclusion(exclusion);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await http.delete(`/api/exclusion-filiale/${selectedExclusion.id}`);
      alert('Exonération désactivée avec succès');
      setShowDeleteModal(false);
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await http.post('/api/exclusion-filiale', {
        filiale_id: parseInt(formData.filiale_id),
        titre_id: parseInt(formData.titre_id),
        type_exclusion: formData.type_exclusion
      });
      alert('Exonération ajoutée avec succès');
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const filteredExclusions = selectedFilialeFilter
    ? exclusions.filter(e => e.filiale_id === parseInt(selectedFilialeFilter))
    : exclusions;

  const filteredBySearch = filteredExclusions.filter(e =>
    e.filiale_libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.titre_libelle?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filtrer les titres par filiale pour l'affichage
  const titres = (!isConsolidated && filialeId && filialeId !== 'all')
    ? allTitres.filter(t => t.filiale_id === parseInt(filialeId))
    : allTitres;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Exonérations par Filiale"
        subtitle="Gérer les exonérations de charges par filiale et titre"
        actions={[
          {
            label: 'Ajouter',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadData,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par filiale ou titre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            {!isConsolidated && (
              <select
                value={selectedFilialeFilter}
                onChange={(e) => setSelectedFilialeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                <option value="">Toutes les filiales</option>
                {filiales.map((f) => (
                  <option key={f.idfiliale} value={f.idfiliale}>
                    {f.libelle}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Liste des exonérations</h3>
            <p className="text-sm text-gray-500 mt-1">{filteredBySearch.length} exonération(s)</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filiale</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type d'exonération</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredBySearch.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      Aucune exonération trouvée
                    </td>
                  </tr>
                ) : (
                  filteredBySearch.map((exclusion) => (
                    <tr key={exclusion.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {exclusion.filiale_libelle}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{exclusion.titre_libelle}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {typeExclusionOptions.find(opt => opt.value === exclusion.type_exclusion)?.label || exclusion.type_exclusion}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {exclusion.actif ? (
                          <span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                            Actif
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                            Inactif
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {exclusion.actif && (
                          <button
                            onClick={() => handleDelete(exclusion)}
                            className="text-red-600 hover:text-red-800"
                            title="Désactiver"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal d'ajout */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border border-blue-100 flex flex-col">
            <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4 px-6">
              <h3 className="text-lg font-bold text-blue-900">Ajouter une exonération</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
              <div className="p-6 space-y-4">
                {isConsolidated && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Filiale <span className="text-red-500">*</span></label>
                    <select
                      value={formData.filiale_id}
                      onChange={(e) => setFormData({ ...formData, filiale_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      required
                    >
                      <option value="">Sélectionner une filiale</option>
                      {filiales.map((f) => (
                        <option key={f.idfiliale} value={f.idfiliale}>
                          {f.libelle}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!isConsolidated && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Filiale</label>
                    <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                      {currentFiliale?.libelle || 'Non sélectionnée'}
                    </div>
                  </div>
                )}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Titre <span className="text-red-500">*</span></label>
                  <select
                    value={formData.titre_id}
                    onChange={(e) => setFormData({ ...formData, titre_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    required
                  >
                    <option value="">Sélectionner un titre</option>
                    {titres.map((t) => (
                      <option key={t.idtitre} value={t.idtitre}>
                        {t.nom_developpe || t.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Type d'exonération <span className="text-red-500">*</span></label>
                  <select
                    value={formData.type_exclusion}
                    onChange={(e) => setFormData({ ...formData, type_exclusion: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    required
                  >
                    {typeExclusionOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-blue-100 p-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {showDeleteModal && selectedExclusion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-red-100">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-red-900">Désactiver l'exonération</h3>
              <p className="text-sm text-gray-600 mt-2">
                Êtes-vous sûr de vouloir désactiver l'exonération pour le titre "{selectedExclusion.titre_libelle}" ?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Filiale : {selectedExclusion.filiale_libelle}<br />
                Type : {typeExclusionOptions.find(opt => opt.value === selectedExclusion.type_exclusion)?.label}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Désactiver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurationExoneration;
